import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DateRangePicker from "react-bootstrap-daterangepicker";
import './worksheet.css';
import '../../css/SearchFilterPanel.css';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { getSavedViewMode, saveViewMode } from '../../utils/viewPersistence';
import CommonTable from '../components/CommonTable/CommonTable';
import EmptyState from '../components/EmptyState/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { getCurrentUserRole, isBranchAdmin, isDepartmentAdmin, isSubDepartmentAdmin } from '../../utils/roleAccess';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import ToolbarFilterInfo from '../layouts/nav/ToolbarFilterInfo';

// Debounce utility
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const WORKSHEET_STATUS_LEGEND = [
  { key: 'to-do', dotClass: 'status-to-do', label: 'Pending' },
  { key: 'in-progress', dotClass: 'status-in-progress', label: 'In Progress' },
  { key: 'done', dotClass: 'status-done', label: 'Completed' },
  { key: 'postponed', dotClass: 'status-postponed', label: 'Blocked' },
  { key: 'paused', dotClass: 'status-paused', label: 'Paused' },
  { key: 'in-partial', dotClass: 'status-in-partial', label: 'Partial' },
];

const Worksheet = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupedTasks, setGroupedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize filters from URL params or defaults
  const getInitialFilters = () => {
    const startDateParam = searchParams.get('start_date');
    const endDateParam = searchParams.get('end_date');
    const userParam = searchParams.get('user');
    const branchParam = searchParams.get('branch');
    const departmentParam = searchParams.get('department');
    const subDepartmentParam = searchParams.get('sub_department');
    const statusParam = searchParams.get('status');

    return {
      startDate: startDateParam ? new Date(startDateParam) : new Date(),
      endDate: endDateParam ? new Date(endDateParam) : new Date(),
      corporate_user: userParam || '',
      branch_id: branchParam || '',
      department_id: departmentParam || '',
      sub_department_id: subDepartmentParam || '',
      status: statusParam || '',
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);
  const [summary, setSummary] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('worksheet', 'table')); // Default to table view
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [searchDropDown, setSearchDropDown] = useState(false);
  const filterRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [openDropdown, setOpenDropdown] = useState(null); // Track which task dropdown is open (for card view only)
  const isInitialMount = useRef(true); // Track initial mount to skip first filter change

  // Status map for converting status labels to numeric values (same as task list)
  const statusMap = {
    'Pending': 0,
    'In Progress': 1,
    'Blocked': 2,
    'Completed': 3,
    'Paused': 4,
    'Partial': 5,
  };

  // State for role-based field disabling
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const [isDepartmentDisabled, setIsDepartmentDisabled] = useState(false);
  const [isSubDepartmentDisabled, setIsSubDepartmentDisabled] = useState(false);

  // Separate state for filter panel (not applied until Apply button is clicked)
  const [tempFilters, setTempFilters] = useState({
    startDate: new Date(),
    endDate: new Date(),
    corporate_user: '',
    branch_id: '',
    department_id: '',
    sub_department_id: '',
    status: ''
  });

  // Helper function to format dates for API
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Update URL parameters when filters change
  const updateURLParams = (newFilters) => {
    const params = new URLSearchParams();

    if (newFilters.startDate) {
      params.set('start_date', formatDate(newFilters.startDate));
    }
    if (newFilters.endDate) {
      params.set('end_date', formatDate(newFilters.endDate));
    }
    if (newFilters.corporate_user) {
      params.set('user', newFilters.corporate_user);
    }
    if (newFilters.branch_id) {
      params.set('branch', newFilters.branch_id);
    }
    if (newFilters.department_id) {
      params.set('department', newFilters.department_id);
    }
    if (newFilters.sub_department_id) {
      params.set('sub_department', newFilters.sub_department_id);
    }
    if (newFilters.status) {
      params.set('status', newFilters.status);
    }

    // Preserve navbar search param when rebuilding filter params
    const currentSearch = searchParams.get('search') || searchTerm || '';
    if (currentSearch) {
      params.set('search', currentSearch);
    }

    setSearchParams(params);
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Fetch corporate users for filter dropdown
  const fetchCorporateUsers = async (branchId = null, departmentId = null, subDepartmentId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const params = new URLSearchParams();
        params.append('corporateId', parseInt(userData.corporateId));
        
        // Add branch, department, and sub_department parameters if provided
        if (branchId) {
          params.append('branch', branchId);
        }
        
        if (departmentId) {
          params.append('department', departmentId);
        }

        if (subDepartmentId) {
          params.append('sub_department', subDepartmentId);
        }

        params.append('limit', 1000);
        params.append('page', 1);

        const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
        setAvailableUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching corporate users:', error);
    }
  };

  // Fetch branches for filter dropdown
  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const response = await axiosInstance.get(`/settings/branches?corporate_id=${userData.corporateId}`);
        const branchesData = response.data.data || response.data || [];
        console.log('Branch data structure:', branchesData);
        setBranches(branchesData);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // Fetch departments for filter dropdown
  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        const response = await axiosInstance.get(`/settings/departments?corporate_id=${userData.corporateId}`);
        const departmentsData = response.data.data || response.data || [];
        console.log('Department data structure:', departmentsData);
        setDepartments(departmentsData);
        setFilteredDepartments(departmentsData); // Initialize filtered departments with all departments
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Fetch sub departments for filter dropdown
  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {
        let res;
        try {
          res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${userData.corporateId}`);
        } catch (e1) {
          res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${userData.corporateId}`);
        }
        const subDepartmentsData = res.data.data || res.data || [];
        console.log('Sub Department data structure:', subDepartmentsData);
        setSubDepartments(subDepartmentsData);
        setFilteredSubDepartments(subDepartmentsData); // Initialize filtered sub departments with all sub departments
      }
    } catch (error) {
      console.error('Error fetching sub departments:', error);
    }
  };

  // Function to filter sub departments based on selected branch and/or department
  const filterSubDepartmentsByDepartment = useCallback(async (departmentId, branchId = null) => {
    if (!departmentId && !branchId) {
      // If no department or branch selected, show all sub departments
      setFilteredSubDepartments(subDepartments);
    } else {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        
        // Build URL with branch and/or department filters
        let url = `/settings/subDepartments?corporate_id=${corporateId}`;
        if (branchId) {
          url += `&branch_id=${branchId}`;
        }
        if (departmentId) {
          url += `&department_id=${departmentId}`;
        }
        
        // Call API to fetch sub departments filtered by selected branch and/or department
        let res;
        try {
          res = await axiosInstance.get(url);
        } catch (e1) {
          // Fallback to lowercase endpoint
          url = url.replace('/settings/subDepartments', '/settings/subdepartments');
          res = await axiosInstance.get(url);
        }
        const subDepartmentsData = res.data;
        const filteredSubDepts = subDepartmentsData.data || subDepartmentsData || [];
        setFilteredSubDepartments(filteredSubDepts);
      } catch (error) {
        console.error('Error fetching sub departments by branch/department:', error);
        // Fallback to filtering from existing data if API call fails
        let filtered = subDepartments;
        if (branchId) {
          // Filter by branch first if branch is provided
          filtered = filtered.filter(subDept => {
            const dept = departments.find(d => d.id === (subDept.department_id || subDept.DepartmentId || subDept.departmentId));
            return dept && String(dept.branch_id) === String(branchId);
          });
        }
        if (departmentId) {
          // Then filter by department if department is provided
          filtered = filtered.filter(subDept => {
            const subDeptId = subDept.department_id || subDept.DepartmentId || subDept.departmentId;
            return String(subDeptId) === String(departmentId);
          });
        }
        setFilteredSubDepartments(filtered);
      }
    }
  }, [subDepartments, departments]);

  // Function to filter departments based on selected branch
  const filterDepartmentsByBranch = useCallback(async (branchId) => {
    console.log('=== FILTERING DEPARTMENTS ===');
    console.log('Branch ID:', branchId);
    console.log('Available branches:', branches);
    console.log('Available departments:', departments);
    
    if (!branchId) {
      // If no branch selected, show all departments
      console.log('No branch selected, showing all departments');
      setFilteredDepartments(departments);
    } else {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        
        // Call API to fetch departments filtered by selected branch
        const response = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${branchId}`);
        const departmentsData = response.data;
        const filteredDepts = departmentsData.data || departmentsData || [];
        console.log('Filtered departments from API:', filteredDepts);
        setFilteredDepartments(filteredDepts);
      } catch (error) {
        console.error('Error fetching departments by branch:', error);
        // Fallback to filtering from existing data if API call fails
        const selectedBranch = branches.find(branch => String(branch.id) === String(branchId));
        console.log('Selected branch:', selectedBranch);
        
        if (selectedBranch) {
          // Filter departments that belong to the selected branch
          const filtered = departments.filter(dept => {
            const matches = dept.branch_name === selectedBranch.name || 
                   String(dept.branch_id) === String(branchId) ||
                   String(dept.branch_id) === String(selectedBranch.id);
            console.log(`Department: ${dept.name}, branch_name: ${dept.branch_name}, branch_id: ${dept.branch_id}, matches: ${matches}`);
            return matches;
          });
          console.log('Filtered departments (fallback):', filtered);
          setFilteredDepartments(filtered);
        } else {
          console.log('Branch not found, showing all departments');
          setFilteredDepartments(departments);
        }
      }
    }
    console.log('=== END FILTERING ===');
  }, [departments, branches]);

  // Fetch worksheet data
  const fetchWorksheetData = async (showLoading = false, overrideFilters = null) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) {
        throw new Error('Corporate ID not found');
      }

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const effectiveFilters = overrideFilters || filters;

      const params = new URLSearchParams({
        corporate_id: userData.corporateId,
        start_date: formatDate(effectiveFilters.startDate),
        end_date: formatDate(effectiveFilters.endDate),
        page: 1,
        limit: 5000
      });

      if (effectiveFilters.corporate_user) {
        params.append('corporate_user', effectiveFilters.corporate_user);
      }

      if (effectiveFilters.branch_id) {
        params.append('branch_id', effectiveFilters.branch_id);
      }

      if (effectiveFilters.department_id) {
        params.append('department_id', effectiveFilters.department_id);
      }

      if (effectiveFilters.sub_department_id) {
        params.append('sub_department_id', effectiveFilters.sub_department_id);
      }

      if (effectiveFilters.status) {
        const numericStatus = statusMap[effectiveFilters.status] ?? '';
        if (numericStatus !== '') {
          params.append('status', numericStatus);
        }
      }

      if (debouncedSearchTerm.trim()) {
        params.append('search', debouncedSearchTerm.trim());
      }

      const response = await axiosInstance.get(`/task/list-corporate-tasks?${params.toString()}`);
      const result = response.data;

      if (result.success) {
        setGroupedTasks(result.data.grouped_tasks || []);
        setSummary(result.data.summary || {});
        setPagination(result.data.pagination || {});
      } else {
        throw new Error(result.message || 'Failed to fetch worksheet data');
      }
    } catch (error) {
      console.error('Error fetching worksheet data:', error);
      setError(error.message);
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Initialize role-based filters
  const initializeRoleBasedFilters = useCallback(async () => {
    try {
      const role = getCurrentUserRole();
      
      if (isBranchAdmin(role) || isDepartmentAdmin(role) || isSubDepartmentAdmin(role)) {
        const userData = JSON.parse(localStorage.getItem('user'));
        const userId = userData?.id;
        
        if (userId) {
          try {
            const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
            const userDetails = userDetailsRes.data;
            
            const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
            const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
            const subDepartmentId = userDetails?.SubDepartment?.id || userDetails?.SubDepartmentId;
            
            // Get current filters to preserve other filter values (like dates)
            const currentFilters = getInitialFilters();
            
            // Branch admin: prefill and disable branch field
            if (isBranchAdmin(role)) {
              if (branchId) {
                const branchIdStr = String(branchId);
                const updatedFilters = {
                  ...currentFilters,
                  branch_id: branchIdStr
                };
                setFilters(updatedFilters);
                setTempFilters(updatedFilters);
                setIsBranchDisabled(true);
                // Filter departments by branch
                await filterDepartmentsByBranch(branchIdStr);
                // Fetch users for this branch
                fetchCorporateUsers(branchIdStr, '', '');
                // Update URL params
                updateURLParams(updatedFilters);
              }
            }
            
            // Department admin: prefill and disable branch and department fields
            if (isDepartmentAdmin(role)) {
              if (branchId) {
                const branchIdStr = String(branchId);
                let updatedFilters = {
                  ...currentFilters,
                  branch_id: branchIdStr
                };
                setFilters(updatedFilters);
                setTempFilters(updatedFilters);
                setIsBranchDisabled(true);
                // Filter departments by branch
                await filterDepartmentsByBranch(branchIdStr);
                // Update URL params for branch
                updateURLParams(updatedFilters);
                
                if (departmentId) {
                  const departmentIdStr = String(departmentId);
                  // Wait a bit for departments to be loaded, then set department
                  setTimeout(() => {
                    updatedFilters = {
                      ...currentFilters,
                      branch_id: branchIdStr,
                      department_id: departmentIdStr
                    };
                    setFilters(updatedFilters);
                    setTempFilters(updatedFilters);
                    setIsDepartmentDisabled(true);
                    // Fetch users for this branch and department
                    fetchCorporateUsers(branchIdStr, departmentIdStr, '');
                    // Update URL params for both branch and department
                    updateURLParams(updatedFilters);
                  }, 300);
                }
              }
            }
            
            // Sub department admin: prefill and disable branch, department, and sub department fields
            if (isSubDepartmentAdmin(role)) {
              if (branchId) {
                const branchIdStr = String(branchId);
                let updatedFilters = {
                  ...currentFilters,
                  branch_id: branchIdStr
                };
                setFilters(updatedFilters);
                setTempFilters(updatedFilters);
                setIsBranchDisabled(true);
                // Filter departments by branch
                await filterDepartmentsByBranch(branchIdStr);
                // Update URL params for branch
                updateURLParams(updatedFilters);
                
                if (departmentId) {
                  const departmentIdStr = String(departmentId);
                  // Wait a bit for departments to be loaded, then set department
                  setTimeout(async () => {
                    updatedFilters = {
                      ...currentFilters,
                      branch_id: branchIdStr,
                      department_id: departmentIdStr
                    };
                    setFilters(updatedFilters);
                    setTempFilters(updatedFilters);
                    setIsDepartmentDisabled(true);
                    // Filter sub departments by department and branch
                    await filterSubDepartmentsByDepartment(departmentIdStr, branchIdStr);
                    
                    if (subDepartmentId) {
                      const subDepartmentIdStr = String(subDepartmentId);
                      // Wait a bit more for sub departments to be loaded, then set sub department
                      setTimeout(() => {
                        updatedFilters = {
                          ...currentFilters,
                          branch_id: branchIdStr,
                          department_id: departmentIdStr,
                          sub_department_id: subDepartmentIdStr
                        };
                        setFilters(updatedFilters);
                        setTempFilters(updatedFilters);
                        setIsSubDepartmentDisabled(true);
                        // Fetch users for this branch, department, and sub department
                        fetchCorporateUsers(branchIdStr, departmentIdStr, subDepartmentIdStr);
                        // Update URL params for all three
                        updateURLParams(updatedFilters);
                      }, 300);
                    }
                  }, 300);
                }
              }
            }
          } catch (err) {
            console.error('Error fetching current user details for role-based filters:', err);
          }
        }
      }
    } catch (err) {
      console.error('Error initializing role-based filters:', err);
    }
  }, [filterDepartmentsByBranch, filterSubDepartmentsByDepartment]);

  useEffect(() => {
    const initializeAll = async () => {
      await fetchBranches();
      await fetchDepartments();
      await fetchSubDepartments();
      // Initialize role-based filters after branches, departments, and sub departments are loaded
      await initializeRoleBasedFilters();
      // Fetch users after role-based filters are set
      fetchCorporateUsers();
      // Initial data load with loading spinner
      await fetchWorksheetData(true);
      // Mark initial mount as complete after initial fetch
      isInitialMount.current = false;
    };
    
    initializeAll();
  }, []);


  // Sync filters when URL parameters change (browser back/forward)
  useEffect(() => {
    const newFilters = getInitialFilters();
    setFilters(newFilters);
  }, [searchParams]);

  // Sync searchTerm FROM URL when navbar writes ?search=
  useEffect(() => {
    const next = searchParams.get('search') || '';
    setSearchTerm((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  // Fetch data when filters or search term change (without loading spinner)
  useEffect(() => {
    // Skip if this is the initial mount (handled by the other useEffect)
    if (!isInitialMount.current) {
      fetchWorksheetData(false);
    }
  }, [filters, debouncedSearchTerm]);

  // Initialize tempFilters when filters change
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  // Filter departments when branch filter changes or when departments are loaded
  useEffect(() => {
    if (departments.length > 0) {
      filterDepartmentsByBranch(filters.branch_id);
    }
  }, [filters.branch_id, filterDepartmentsByBranch]);

  // Filter sub departments when branch or department filter changes or when sub departments are loaded
  useEffect(() => {
    if (subDepartments.length > 0) {
      filterSubDepartmentsByDepartment(filters.department_id, filters.branch_id);
    }
  }, [filters.department_id, filters.branch_id, filterSubDepartmentsByDepartment]);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    
    // If branch is changed, filter departments and reset department selection
    if (field === 'branch_id') {
      filterDepartmentsByBranch(value);
      // Refetch sub departments filtered by branch
      filterSubDepartmentsByDepartment('', value);
      // Reset department, sub department and user selection when branch changes
      newFilters.department_id = '';
      newFilters.sub_department_id = '';
      newFilters.corporate_user = '';
      // Refetch users based on new branch selection
      fetchCorporateUsers(value, '', '');
    } else if (field === 'department_id') {
      // Filter sub departments by department and branch, and reset sub department and user selection
      filterSubDepartmentsByDepartment(value, filters.branch_id);
      newFilters.sub_department_id = '';
      newFilters.corporate_user = '';
      // Refetch users based on new department selection
      fetchCorporateUsers(filters.branch_id, value, '');
    } else if (field === 'sub_department_id') {
      // Reset user selection when sub department changes
      newFilters.corporate_user = '';
      // Refetch users based on new sub department selection (include sub_department_id)
      fetchCorporateUsers(filters.branch_id, filters.department_id, value);
    }
    
    setFilters(newFilters);
    updateURLParams(newFilters);
  };

  const resetFilters = () => {
    const role = getCurrentUserRole();
    let newFilters = {
      startDate: new Date(),
      endDate: new Date(),
      corporate_user: '',
      branch_id: '',
      department_id: '',
      sub_department_id: '',
      status: ''
    };
    
    // If user is branch admin, department admin, or sub department admin, preserve their branch/department/sub_department
    if (isBranchAdmin(role) || isDepartmentAdmin(role) || isSubDepartmentAdmin(role)) {
      const userData = JSON.parse(localStorage.getItem('user'));
      const userId = userData?.id;
      
      if (userId) {
        // Get user details to restore branch/department/sub_department
        axiosInstance.get(`/auth/details/${userId}`)
          .then(userDetailsRes => {
            const userDetails = userDetailsRes.data;
            const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
            const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
            const subDepartmentId = userDetails?.SubDepartment?.id || userDetails?.SubDepartmentId;
            
            if (isBranchAdmin(role) && branchId) {
              newFilters.branch_id = String(branchId);
            }
            
            if (isDepartmentAdmin(role)) {
              if (branchId) {
                newFilters.branch_id = String(branchId);
              }
              if (departmentId) {
                newFilters.department_id = String(departmentId);
              }
            }
            
            if (isSubDepartmentAdmin(role)) {
              if (branchId) {
                newFilters.branch_id = String(branchId);
              }
              if (departmentId) {
                newFilters.department_id = String(departmentId);
              }
              if (subDepartmentId) {
                newFilters.sub_department_id = String(subDepartmentId);
              }
            }
            
            setFilters(newFilters);
            setTempFilters(newFilters);
            setSearchTerm('');
            if (newFilters.branch_id) {
              filterDepartmentsByBranch(newFilters.branch_id);
              if (newFilters.department_id) {
                filterSubDepartmentsByDepartment(newFilters.department_id, newFilters.branch_id);
              } else {
                // If no department, still filter sub departments by branch
                filterSubDepartmentsByDepartment('', newFilters.branch_id);
              }
              fetchCorporateUsers(newFilters.branch_id, newFilters.department_id || '', newFilters.sub_department_id || '');
            } else {
              setFilteredDepartments(departments);
              setFilteredSubDepartments(subDepartments);
              fetchCorporateUsers();
            }
            updateURLParams(newFilters);
            fetchWorksheetData(false, newFilters);
          })
          .catch(err => {
            console.error('Error fetching user details during reset:', err);
            // Fallback to normal reset
            setFilters(newFilters);
            setTempFilters(newFilters);
            setSearchTerm('');
            setFilteredDepartments(departments);
            fetchCorporateUsers();
            updateURLParams(newFilters);
            fetchWorksheetData(false, newFilters);
          });
        return;
      }
    }
    
    // Normal reset for other roles
    setFilters(newFilters);
    setTempFilters(newFilters);
    setSearchTerm('');
    setFilteredDepartments(departments); // Reset to show all departments
    setFilteredSubDepartments(subDepartments); // Reset to show all sub departments
    // Refetch all users when filters are reset
    fetchCorporateUsers();
    updateURLParams(newFilters);
    // Fetch worksheet data with the new filters
    fetchWorksheetData(false, newFilters);
  };

  const handleClickOutside = (event) => {
    // Don't close filter if click happens inside the daterangepicker overlay
    const clickedInsideDatePicker = !!(event.target.closest && (
      event.target.closest('.daterangepicker') ||
      event.target.closest('.drp-buttons') ||
      event.target.closest('.calendar-table')
    ));

    if (filterRef.current && !filterRef.current.contains(event.target) && !clickedInsideDatePicker) {
      setSearchDropDown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleViewAllUser = (userId) => {
    setFilters({ ...filters, corporate_user: userId });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode('worksheet', mode);
  };

  const handleTempFilterChange = (field, value) => {
    // If branch is changed, filter departments and reset department selection
    if (field === 'branch_id') {
      filterDepartmentsByBranch(value);
      // Refetch sub departments filtered by branch
      filterSubDepartmentsByDepartment('', value);
      // Reset department, sub department and user selection when branch changes
      setTempFilters(prev => ({
        ...prev,
        [field]: value,
        department_id: '',
        sub_department_id: '',
        corporate_user: ''
      }));
      // Refetch users based on new branch selection
      fetchCorporateUsers(value, '', '');
    } else if (field === 'department_id') {
      // Filter sub departments by department and branch, and reset sub department and user selection
      filterSubDepartmentsByDepartment(value, tempFilters.branch_id);
      setTempFilters(prev => ({
        ...prev,
        [field]: value,
        sub_department_id: '',
        corporate_user: '' // Reset user selection when department changes
      }));
      // Refetch users based on new department selection
      fetchCorporateUsers(tempFilters.branch_id, value, '');
    } else if (field === 'sub_department_id') {
      setTempFilters(prev => ({
        ...prev,
        [field]: value,
        corporate_user: '' // Reset user selection when sub department changes
      }));
      // Refetch users based on new sub department selection (include sub_department_id)
      fetchCorporateUsers(tempFilters.branch_id, tempFilters.department_id, value);
    } else {
      setTempFilters(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    updateURLParams(tempFilters);
    setSearchDropDown(false);
  };

  const cancelFilters = () => {
    resetFilters();
    setSearchDropDown(false);
  };

  const toggleTaskDropdown = (taskId, event, userId = null) => {
    event.stopPropagation();
    // Create unique identifier: use userId-taskId for complete uniqueness
    const uniqueId = userId ? `${userId}-${taskId}` : taskId;
    
    if (openDropdown === uniqueId) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(uniqueId);
    }
  };


  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) {
        throw new Error('Corporate ID not found');
      }

      const response = await axiosInstance.post(`/task/statuschange`, {
        taskId: taskId,
        status: newStatus,
        UpdatedBy: userData.id
      });

      if (response.data.success) {
        // Update the task status in the local state
        setGroupedTasks(prevGroups =>
          prevGroups.map(group => ({
            ...group,
            tasks: group.tasks.map(task =>
              task.id === taskId
                ? { ...task, status: newStatus }
                : task
            )
          }))
        );

        toast.success('Task status updated successfully!');
        setOpenDropdown(null);
      } else {
        throw new Error(response.data.message || 'Failed to update task status');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      notifyError(error.message);
    }
  };

  const handleTaskTitleClick = (taskId, event) => {
    event.stopPropagation();
    navigate(`/task/taskdetails/${taskId}`);
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const getStatusClass = (status) => {
    // Handle both numeric and string status values
    const statusValue = typeof status === 'number' ? status : status?.toLowerCase();

    switch (statusValue) {
      case 3:
      case 'done':
        return 'status-done';
      case 1:
      case 'in progress':
        return 'status-in-progress';
      case 2:
      case 'postponed':
        return 'status-postponed';
      case 0:
      case 'to do':
        return 'status-to-do';
      case 4:
      case 'paused':
        return 'status-paused';
      case 5:
      case 'partial':
        return 'status-in-partial';
      default:
        return 'status-to-do';
    }
  };

  const getStatusLabel = (status) => {
    // Handle both numeric and string status values
    const statusValue = typeof status === 'number' ? status : status?.toLowerCase();

    switch (statusValue) {
      case 3:
      case 'done':
        return 'Completed';
      case 1:
      case 'in progress':
        return 'In Progress';
      case 2:
      case 'postponed':
        return 'Blocked';
      case 0:
      case 'to do':
        return 'Pending';
      case 4:
      case 'paused':
        return 'Paused';
      case 5:
      case 'partial':
        return 'Partial';
      default:
        return 'Pending';
    }
  };


  const renderDescription = (description) => {
    if (!description || description.trim() === '') {
      return <p className="text-muted">No description provided</p>;
    }

    // Check if description contains HTML tags
    if (description.includes('<') && description.includes('>')) {
      return <div dangerouslySetInnerHTML={{ __html: description }} />;
    }

    return <p>{description}</p>;
  };

  // Flatten all tasks for table view
  const getAllTasks = () => {
    const allTasks = [];
    groupedTasks.forEach(group => {
      group.tasks.forEach(task => {
        allTasks.push({
          ...task,
          user_name: group.user?.full_name || 'Unknown User',
          user_role: task.user_role || 'Unknown Role',
          user_id: group.user?.id
        });
      });
    });
    return allTasks;
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'user',
      header: 'User',
      render: (row, rowIndex) => {
        const allTasks = getAllTasks();
        const isFirstTaskForUser = rowIndex === 0 || allTasks[rowIndex - 1].user_id !== row.user_id;
        return (
          <div className="user-info">
            {isFirstTaskForUser && (
              <div className="user-name">{row.user_name}</div>
            )}
          </div>
        );
      },
      cellClassName: 'user-cell',
    },
    {
      key: 'task',
      header: 'Task',
      render: (row) => (
        <div
          className="task-title clickable-title"
          onClick={(e) => handleTaskTitleClick(row.id, e)}
        >
          {row.title}
        </div>
      ),
      cellClassName: 'task-cell',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`status-badge ${getStatusClass(row.status)}`}>
          {getStatusLabel(row.status)}
        </span>
      ),
      cellClassName: 'status-cell',
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => (
        <span className={`priority-badge ${getPriorityClass(row.priority)}`}>
          {row.priority}
        </span>
      ),
      cellClassName: 'priority-cell',
    },
    {
      key: 'startDate',
      header: 'Start Date',
      accessor: (row) => row.start_date ? new Date(row.start_date).toLocaleDateString() : 'N/A',
      cellClassName: 'date-cell',
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      accessor: (row) => row.due_date ? new Date(row.due_date).toLocaleDateString() : 'N/A',
      cellClassName: 'date-cell',
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => (
        <span className="role-badge">{row.user_role}</span>
      ),
      cellClassName: 'role-cell',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    const actions = [
      {
        label: 'View',
        icon: 'fa fa-eye',
        onClick: () => navigate(`/task/taskdetails/${row.id}`),
      },
    ];

    // Start/Pause button - show if not completed
    if (row.status !== 3) {
      const isStart = row.status === 0 || row.status === 4 || row.status === 2;
      actions.push({
        label: isStart ? 'Start' : 'Pause',
        icon: isStart ? 'fas fa-play' : 'fas fa-pause',
        onClick: () => handleStatusChange(row.id, isStart ? 1 : 4),
      });
    }

    // Block button - show if not blocked and not completed
    if (row.status !== 2 && row.status !== 3) {
      actions.push({
        label: 'Block',
        icon: 'fas fa-ban',
        onClick: () => handleStatusChange(row.id, 2),
      });
    }

    // Finish button - show if not completed
    if (row.status !== 3) {
      actions.push({
        label: 'Finish',
        icon: 'fas fa-check',
        onClick: () => handleStatusChange(row.id, 3),
      });
    }

    // Re-open button - show only when task is completed (status 3)
    if (row.status === 3) {
      actions.push({
        label: 'Re-open',
        icon: 'fas fa-undo',
        onClick: () => handleStatusChange(row.id, 0),
      });
    }

    // Edit button
    actions.push({
      label: 'Edit',
      icon: 'fa fa-edit',
      onClick: () => navigate('/task/edittask', { state: { taskid: row.id } }),
    });

    return actions;
  };

  const renderTableView = () => {
    const allTasks = getAllTasks();
    
    const emptyMessage = 'No tasks available for the selected criteria.';

    return (
      <CommonTable
        columns={columns}
        data={allTasks}
        actions={getActions}
        emptyMessage={emptyMessage}
        emptyStateIcon="fas fa-tasks"
        getRowId={(row, index) => `${row.id}-${index}`}
        maxHeight="85vh"
        minHeight="530px"
      />
    );
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const worksheetFilterInfo = useMemo(() => {
    const items = [];
    if (filters.startDate || filters.endDate) {
      const start = formatDisplayDate(filters.startDate);
      const end = formatDisplayDate(filters.endDate);
      items.push({
        key: 'date',
        icon: 'fas fa-calendar-alt',
        label: 'Date',
        value: start && end && start !== end ? `${start} – ${end}` : (start || end),
      });
    }
    if (filters.branch_id) {
      const branch = branches.find((b) => String(b.id) === String(filters.branch_id));
      items.push({
        key: 'branch',
        icon: 'fas fa-code-branch',
        label: 'Branch',
        value: branch?.name || filters.branch_id,
      });
    }
    if (filters.department_id) {
      const dept = (filteredDepartments.length ? filteredDepartments : departments).find(
        (d) => String(d.id) === String(filters.department_id)
      );
      items.push({
        key: 'department',
        icon: 'fas fa-building',
        label: 'Department',
        value: dept?.name || filters.department_id,
      });
    }
    if (filters.sub_department_id) {
      const sub = (filteredSubDepartments.length ? filteredSubDepartments : subDepartments).find(
        (s) => String(s.id) === String(filters.sub_department_id)
      );
      items.push({
        key: 'sub_department',
        icon: 'fas fa-sitemap',
        label: 'Sub dept',
        value: sub?.name || filters.sub_department_id,
      });
    }
    if (filters.corporate_user) {
      const user = availableUsers.find((u) => String(u.id) === String(filters.corporate_user));
      items.push({
        key: 'user',
        icon: 'fas fa-user',
        label: 'Person',
        value: user?.full_name || user?.name || filters.corporate_user,
      });
    }
    if (filters.status) {
      items.push({
        key: 'status',
        icon: 'fas fa-flag',
        label: 'Status',
        value: filters.status,
      });
    }
    return items;
  }, [
    filters,
    branches,
    departments,
    filteredDepartments,
    subDepartments,
    filteredSubDepartments,
    availableUsers,
  ]);

  const worksheetToolbar = useMemo(
    () => (
      <>
        <div className="page-toolbar-bar__start">
          <div className="toolbar-status-legend" aria-label="Status legend">
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-to-do" />
              <span>Pending</span>
            </div>
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-in-progress" />
              <span>In Progress</span>
            </div>
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-done" />
              <span>Completed</span>
            </div>
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-postponed" />
              <span>Blocked</span>
            </div>
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-paused" />
              <span>Paused</span>
            </div>
            <div className="toolbar-status-legend__item">
              <span className="legend-dot status-in-partial" />
              <span>Partial</span>
            </div>
          </div>
          <ToolbarFilterInfo
            items={worksheetFilterInfo}
            statuses={WORKSHEET_STATUS_LEGEND}
            ariaLabel="Status legend and active filters"
          />
        </div>
        <div className="page-toolbar-bar__end">
          <div className="view-toggle-container">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('card')}
              title="Card View"
              aria-label="Card View"
            >
              <i className="fas fa-th-large"></i>
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('table')}
              title="Table View"
              aria-label="Table View"
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
          <button
            type="button"
            className="btn header-toolbar-btn"
            onClick={() => setSearchDropDown(true)}
            title="Filters"
            aria-label="Filters"
          >
            <i className="fas fa-filter"></i>
          </button>
        </div>
      </>
    ),
    [viewMode, worksheetFilterInfo]
  );

  usePageToolbar(worksheetToolbar, [worksheetToolbar]);

  if (loading) {
    return (
      <div className="worksheet-container">
        <LoadingSpinner label="Loading…" />
        <InternalFooter />
      </div>
    );
  }


  return (
    <div className="worksheet-container">
      {/* Filter Panel */}
      {searchDropDown && (
        <div className="filter-wrapper" ref={filterRef}>
          <div className="content">
            <div className="main-filters">
              <div className="filter-form">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Date Range</label>
                    <DateRangePicker
                      initialSettings={{
                        startDate: tempFilters.startDate,
                        endDate: tempFilters.endDate,
                        locale: { format: 'MM/DD/YYYY' },
                        autoApply: true,
                      }}
                      onCallback={(start, end) => {
                        const newFilters = {
                          ...tempFilters,
                          startDate: start.toDate(),
                          endDate: end.toDate(),
                        };
                        setTempFilters(newFilters);
                        setFilters(newFilters);
                        updateURLParams(newFilters);
                        fetchWorksheetData(false, newFilters);
                      }}
                      onApply={(event, picker) => {
                        const start = picker.startDate ? picker.startDate.toDate() : tempFilters.startDate;
                        const end = picker.endDate ? picker.endDate.toDate() : tempFilters.endDate;
                        const newFilters = {
                          ...tempFilters,
                          startDate: start,
                          endDate: end,
                        };
                        setTempFilters(newFilters);
                        setFilters(newFilters);
                        updateURLParams(newFilters);
                        // Ensure data refresh immediately with the new values
                        fetchWorksheetData(false, newFilters);
                      }}
                    >
                      <input
                        type="text"
                        className="form-control input-daterange-timepicker"
                        readOnly
                        placeholder="Select date range"
                      />
                    </DateRangePicker>
                  </div>

                  <div className="filter-group">
                    <label>Filter by Branch</label>
                    <select
                      value={tempFilters.branch_id}
                      onChange={(e) => handleTempFilterChange('branch_id', e.target.value)}
                      disabled={isBranchDisabled}
                    >
                      <option value="">All Branches</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Filter by Department</label>
                    <select
                      value={tempFilters.department_id}
                      onChange={(e) => handleTempFilterChange('department_id', e.target.value)}
                      disabled={isDepartmentDisabled}
                    >
                      <option value="">All Departments</option>
                      {filteredDepartments.map(department => (
                        <option key={department.id} value={department.id}>
                          {`${department.name} ${((department.branch_name) ? `(${department.branch_name})` : '')}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label>Filter by User</label>
                    <select
                      value={tempFilters.corporate_user}
                      onChange={(e) => handleTempFilterChange('corporate_user', e.target.value)}
                    >
                      <option value="">All Users</option>
                      {availableUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {`${user.full_name} ${((user.department_name || user.branch_name) ? `(${user.department_name ? user.department_name + "," : ''}${user.branch_name ? user.branch_name : ''})` : '')}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Filter by Sub Department</label>
                    <select
                      value={tempFilters.sub_department_id}
                      onChange={(e) => handleTempFilterChange('sub_department_id', e.target.value)}
                      disabled={isSubDepartmentDisabled}
                    >
                      <option value="">All Sub Departments</option>
                      {filteredSubDepartments.map(subDepartment => (
                        <option key={subDepartment.id} value={subDepartment.id}>
                          {`${subDepartment.name} ${((subDepartment.department_name) ? `(${subDepartment.department_name})` : '')}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Status</label>
                    <select
                      value={tempFilters.status}
                      onChange={(e) => handleTempFilterChange('status', e.target.value)}
                    >
                      <option value="">Select Status</option>
                      <option>Pending</option>
                      <option>In Progress</option>
                      <option>Blocked</option>
                      <option>Completed</option>
                      <option>Paused</option>
                      <option>Partial</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="filter-actions">
                <button className="search-btn" onClick={applyFilters}>
                  <i className="fas fa-search me-2"></i>
                  Apply Filters
                </button>
                <button className="search-btn" onClick={cancelFilters}>
                  <i className="fas fa-times me-2"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-section">
          <div className="error-card">
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={fetchWorksheetData}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Tasks by User */}
      <div className="tasks-section">
        {viewMode === 'table' ? (
          renderTableView()
        ) : (
          groupedTasks.length === 0 ? (
            <EmptyState
              icon="fas fa-tasks"
              title="No Data Found"
              description="No tasks available for the selected criteria."
            />
          ) : filters.corporate_user ? (
            // Card design when user is selected
            groupedTasks.map((group, groupIndex) => (
              <div key={groupIndex} className="user-group">
                <div className="user-header">
                  <div className="user-info">
                    <h3>
                      {group.user?.full_name || 'Unknown User'}
                    </h3>
                  </div>
                  <span className="task-count">{group.tasks.length} task(s)</span>
                </div>

                <div className={`tasks-list ${filters.corporate_user ? 'user-selected' : ''}`}>
                  {group.tasks.map((task, taskIndex) => (
                    <div
                      key={taskIndex}
                      className="task-row"
                    >
                      <div className="task-info">
                        <div
                          className="task-title clickable-title"
                          onClick={(e) => handleTaskTitleClick(task.id, e)}
                        >
                          {task.title}
                        </div>
                        <div className="task-role">{task.user_role || 'Unknown Role'}</div>
                        <div className="task-dates">
                          <div className="task-date">
                            <span className="date-label">Start:</span>
                            <span className="date-value">{task.start_date ? new Date(task.start_date).toLocaleDateString() : 'N/A'}</span>
                          </div>
                          <div className="task-date">
                            <span className="date-label">Due:</span>
                            <span className="date-value">{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="task-status-priority">
                        {/* Only show dropdown button if user has any available options */}
                        {(() => {
                          const userData = JSON.parse(localStorage.getItem("user"));
                          const hasStatusOptions = task.status !== 3 || // Can finish if not completed
                            (task.status !== 2 && task.status !== 3) || // Can block if not blocked/completed
                            task.status !== 3; // Can start/pause if not completed

                          return (hasStatusOptions) && (
                            <div className="task-actions">
                              <button
                                className={`dropdown-toggle-btn ${openDropdown === `${group.user?.id}-${task.id}` ? 'active' : ''}`}
                                onClick={(e) => toggleTaskDropdown(task.id, e, group.user?.id)}
                              >
                                <i className="fas fa-ellipsis-v"></i>
                              </button>
                            </div>
                          );
                        })()}
                        <div className="task-status">
                          <span className={`status-badge ${getStatusClass(task.status)}`}>
                            {getStatusLabel(task.status)}
                          </span>
                        </div>
                        <div className="task-priority">
                          <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>

                      </div>

                      {/* Status Change Dropdown - only show if there are available options */}
                      {openDropdown === `${group.user?.id}-${task.id}` && (() => {
                        const userData = JSON.parse(localStorage.getItem("user"));
                        // Always show dropdown as there are always available actions:
                        // For completed tasks (status 3): Re-open option
                        // For non-completed tasks: Start/Pause, Block, Finish options
                        const hasStatusOptions = true;

                        return (hasStatusOptions) && (
                          <div className="status-dropdown indivitual-status-dropdown">
                            <div className="status-options">
                              {/* View button */}
                              <button
                                className="status-option status-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/task/taskdetails/${task.id}`);
                                }}
                              >
                                <i className="fa fa-eye" style={{ marginRight: '0.5rem' }}></i>
                                View
                              </button>

                              {/* Start/Pause button - show if not completed */}
                              {task.status !== 3 && (
                                <button
                                  className={`status-option ${task.status === 0 || task.status === 4 || task.status === 2 ? getStatusClass(1) : getStatusClass(4)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(task.id, task.status === 0 || task.status === 4 || task.status === 2 ? 1 : 4);
                                  }}
                                >
                                  <i className={`fas ${task.status === 0 || task.status === 4 || task.status === 2 ? 'fa-play' : 'fa-pause'}`}></i>
                                  {task.status === 0 || task.status === 4 || task.status === 2 ? 'Start' : 'Pause'}
                                </button>
                              )}

                              {/* Block button - show if not blocked and not completed */}
                              {task.status !== 2 && task.status !== 3 && (
                                <button
                                  className={`status-option ${getStatusClass(2)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(task.id, 2);
                                  }}
                                >
                                  <i className="fas fa-ban"></i>
                                  Block
                                </button>
                              )}

                              {/* Finish button - show if not completed */}
                              {task.status !== 3 && (
                                <button
                                  className={`status-option ${getStatusClass(3)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(task.id, 3);
                                  }}
                                >
                                  <i className="fas fa-check"></i>
                                  Finish
                                </button>
                              )}

                              {/* Re-open button - show only when task is completed (status 3) */}
                              {task.status === 3 && (
                                <button
                                  className={`status-option ${getStatusClass(0)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(task.id, 0);
                                  }}
                                >
                                  <i className="fas fa-undo"></i>
                                  Re-open
                                </button>
                              )}

                              <button
                                className="status-option status-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/task/edittask', {
                                    state: { taskid: task.id },
                                  });
                                }}
                              >
                                <i className="fa fa-edit" style={{ marginRight: '0.5rem' }}></i>
                                Edit
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Column layout when no user is selected - users in columns
            <div className="users-columns-container">
              {groupedTasks.map((group, groupIndex) => (
                <div key={groupIndex} className="user-column">
                  <div className="user-header">
                    <div className="user-name">{group.user?.full_name || 'Unknown User'}</div>
                    <div className="task-count">{group.tasks.length} task(s)</div>
                  </div>
                  <div className="user-tasks">
                    <div className="tasks-list-column">
                      {group.tasks.slice(0, 4).map((task, taskIndex) => (
                        <div
                          key={taskIndex}
                          className={`task-item-worksheet ${getStatusClass(task.status)}`}
                          style={{ position: 'relative' }}
                        >
                          <div className="task-content">
                            <span
                              className="task-title clickable-title"
                              onClick={(e) => handleTaskTitleClick(task.id, e)}
                            >
                              {task.title}
                            </span>
                            <span className="task-priority">({task.priority})</span>
                          </div>

                          {/* Only show dropdown button if user has any available options */}
                          {(() => {
                            const userData = JSON.parse(localStorage.getItem("user"));
                            const hasStatusOptions = task.status !== 3 || // Can finish if not completed
                              (task.status !== 2 && task.status !== 3) || // Can block if not blocked/completed
                              task.status !== 3; // Can start/pause if not completed

                            return (hasStatusOptions) && (
                              <div className="task-actions">
                                <button
                                  className={`dropdown-toggle-btn ${openDropdown === `${group.user?.id}-${task.id}` ? 'active' : ''}`}
                                  onClick={(e) => toggleTaskDropdown(task.id, e, group.user?.id)}
                                >
                                  <i className="fas fa-ellipsis-v"></i>
                                </button>
                              </div>
                            );
                          })()}

                          {/* Status Change Dropdown for column view */}
                          {openDropdown === `${group.user?.id}-${task.id}` && (() => {
                            const userData = JSON.parse(localStorage.getItem("user"));
                            const hasStatusOptions = task.status !== 3 || // Can finish if not completed
                              (task.status !== 2 && task.status !== 3) || // Can block if not blocked/completed
                              task.status !== 3; // Can start/pause if not completed

                            return (hasStatusOptions) && (
                              <div className="status-dropdown">
                                <div className="status-options">
                                  {/* View button */}
                                  <button
                                    className="status-option status-view"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/task/taskdetails/${task.id}`);
                                    }}
                                  >
                                    <i className="fa fa-eye" style={{ marginRight: '0.5rem' }}></i>
                                    View
                                  </button>

                                  {/* Start/Pause button - show if not completed */}
                                  {task.status !== 3 && (
                                    <button
                                      className={`status-option ${task.status === 0 || task.status === 4 || task.status === 2 ? getStatusClass(1) : getStatusClass(4)}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(task.id, task.status === 0 || task.status === 4 || task.status === 2 ? 1 : 4);
                                      }}
                                    >
                                      <i className={`fas ${task.status === 0 || task.status === 4 || task.status === 2 ? 'fa-play' : 'fa-pause'}`}></i>
                                      {task.status === 0 || task.status === 4 || task.status === 2 ? 'Start' : 'Pause'}
                                    </button>
                                  )}

                                  {/* Block button - show if not blocked and not completed */}
                                  {task.status !== 2 && task.status !== 3 && (
                                    <button
                                      className={`status-option ${getStatusClass(2)}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(task.id, 2);
                                      }}
                                    >
                                      <i className="fas fa-ban"></i>
                                      Block
                                    </button>
                                  )}

                                  {/* Finish button - show if not completed */}
                                  {task.status !== 3 && (
                                    <button
                                      className={`status-option ${getStatusClass(3)}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(task.id, 3);
                                      }}
                                    >
                                      <i className="fas fa-check"></i>
                                      Finish
                                    </button>
                                  )}

                                  {/* Re-open button - show only when task is completed (status 3) */}
                                  {task.status === 3 && (
                                    <button
                                      className={`status-option ${getStatusClass(0)}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusChange(task.id, 0);
                                      }}
                                    >
                                      <i className="fas fa-undo"></i>
                                      Re-open
                                    </button>
                                  )}
                                  <button
                                    className="status-option status-edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/task/edittask', {
                                        state: { taskid: task.id },
                                      });
                                    }}
                                  >
                                    <i className="fa fa-edit" style={{ marginRight: '0.5rem' }}></i>
                                    Edit
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                    {group.tasks.length > 4 && (
                      <button
                        className="view-all-btn"
                        onClick={() => handleViewAllUser(group.user?.id)}
                      >
                        View All ({group.tasks.length} tasks)
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <ToastContainer />
      <BackToTop />
      <InternalFooter />
    </div>
  );
};

export default Worksheet;
